import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import HardwareInfo from '../../components/HardwareInfo';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, ChevronLeft, ChevronRight, Eye, Edit, Trash2, Copy,
  Monitor, Cpu, HardDrive, Package, Wrench, UserCheck, AlertTriangle,
  ChevronUp, ChevronDown, ChevronsUpDown, Activity, WifiOff, Search, X
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';

const initialFormData = {
  computername: '',
  username: '',
  os: '',
  brand_id: '',
  modelo: '',
  serialnumber: '',
  cpu: '',
  ram: '',
  macaddress: '',
  purchase_date: '',
  status_id: '',
  site_id: '',
  empresa_id: '',
  location_id: '',
  assigned_user_id: '',
  comments: '',
  supplier_id: '',
};

const Workstations = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'workstations');
  const [workstations, setWorkstations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 0 });
  const [stats, setStats] = useState({ 
    total: 0, 
    con_agente: 0, 
    sin_agente: 0, 
    agente_activo: 0, 
    agente_advertencia: 0, 
    agente_critico: 0 
  });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'computername', dir: 'asc' });
  
  // Column filters (inline por columna)
  const [columnFilters, setColumnFilters] = useState({
    equipo: '',
    ip: '',
    modelo: '',
    usuario_id: '',
    cliente: '',
    marca: '',
    os_id: '',
    edicion: '',
    build: '',
    site: '',
    legajo: '',
    ram: '',
  });
  
  // Global search
  const [globalSearch, setGlobalSearch] = useState('');
  
  // Agent status filter (from stats cards)
  const [agenteFilter, setAgenteFilter] = useState(null); // null, 'activo', 'warning', 'offline', 'sin_agente'
  
  // Form options
  const [formOptions, setFormOptions] = useState({ os: [], estados: [], proveedores: [], brands: [], sites: [], empresas: [], ubicaciones: [], usuarios: [], clientes: [] });
  
  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(workstations);
  
  // Modals
  const [editModal, setEditModal] = useState({ open: false, workstation: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, workstation: null });
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/workstations/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/workstations/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchWorkstations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      
      // Add global search
      if (globalSearch) params.append('search', globalSearch);
      
      // Add column filters
      if (columnFilters.equipo) params.append('equipo_filter', columnFilters.equipo);
      if (columnFilters.ip) params.append('ip_filter', columnFilters.ip);
      if (columnFilters.modelo) params.append('modelo_filter', columnFilters.modelo);
      if (columnFilters.usuario_id) params.append('usuario_id', columnFilters.usuario_id);
      if (columnFilters.cliente) params.append('cliente_filter', columnFilters.cliente);
      if (columnFilters.marca) params.append('brand_filter', columnFilters.marca);
      if (columnFilters.os_id) params.append('os_filter', columnFilters.os_id);
      if (columnFilters.edicion) params.append('os_caption_filter', columnFilters.edicion);
      if (columnFilters.build) params.append('build_filter', columnFilters.build);
      if (columnFilters.site) params.append('site_filter', columnFilters.site);
      if (columnFilters.legajo) params.append('legajo_filter', columnFilters.legajo);
      if (columnFilters.ram) params.append('ram_filter', columnFilters.ram);
      
      // Add agent status filter
      if (agenteFilter) params.append('agente_status', agenteFilter);

      const response = await api.get(`/assets/workstations?${params}`);
      setWorkstations(response.data.items);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar workstations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFormOptions();
  }, []);

  useEffect(() => {
    fetchWorkstations();
  }, [pagination.page, pagination.page_size, sortConfig, agenteFilter]);
  
  // Debounced filter effect
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchWorkstations();
    }, 800);
    return () => clearTimeout(t);
  }, [columnFilters, globalSearch]);

  const handleFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  // Clear all filters
  const clearFilters = () => {
    setGlobalSearch('');
    setColumnFilters({ equipo: '', ip: '', modelo: '', usuario_id: '', cliente: '', marca: '', os_id: '', edicion: '', build: '', site: '', legajo: '', ram: '' });
    setAgenteFilter(null);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Check if any filter is active
  const hasActiveFilters = globalSearch || columnFilters.equipo || columnFilters.ip || columnFilters.modelo || columnFilters.usuario_id || columnFilters.cliente || columnFilters.marca || columnFilters.os_id || columnFilters.edicion || columnFilters.build || columnFilters.site || columnFilters.legajo || columnFilters.ram || agenteFilter;

  // Handler for clicking stats cards to filter by agent status
  const handleAgenteFilterClick = (status) => {
    // Toggle: if same filter is clicked, clear it
    if (agenteFilter === status) {
      setAgenteFilter(null);
    } else {
      setAgenteFilter(status);
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  // Edit modal
  const handleEdit = (workstation) => {
    setFormData({
      computername: workstation.computername || '',
      username: workstation.username || '',
      os: workstation.os_id ? String(workstation.os_id) : '',
      brand_id: workstation.brand_id ? String(workstation.brand_id) : '',
      modelo: workstation.modelo || '',
      serialnumber: workstation.serialnumber || '',
      cpu: workstation.cpu || '',
      ram: workstation.ram || '',
      macaddress: workstation.macaddress || '',
      purchase_date: workstation.purchase_date || '',
      status_id: workstation.status_id ? String(workstation.status_id) : '',
      site_id: workstation.site_id ? String(workstation.site_id) : '',
      empresa_id: workstation.empresa_id ? String(workstation.empresa_id) : '',
      location_id: workstation.location_id ? String(workstation.location_id) : '',
      assigned_user_id: workstation.assigned_user_id ? String(workstation.assigned_user_id) : '',
      cliente_id: workstation.cliente_id ? String(workstation.cliente_id) : '',
      comments: workstation.comments || '',
      supplier_id: workstation.supplier_id ? String(workstation.supplier_id) : '',
    });
    setEditModal({ open: true, workstation, isCopy: false });
  };

  // Copy modal
  const handleCopy = (workstation) => {
    setFormData({
      computername: '', // Unique - clear
      username: workstation.username || '',
      os: workstation.os_id ? String(workstation.os_id) : '',
      brand_id: workstation.brand_id ? String(workstation.brand_id) : '',
      modelo: workstation.modelo || '',
      serialnumber: '', // Unique - clear
      cpu: workstation.cpu || '',
      ram: workstation.ram || '',
      macaddress: '', // Unique - clear
      purchase_date: '',
      status_id: workstation.status_id ? String(workstation.status_id) : '',
      site_id: workstation.site_id ? String(workstation.site_id) : '',
      empresa_id: workstation.empresa_id ? String(workstation.empresa_id) : '',
      location_id: workstation.location_id ? String(workstation.location_id) : '',
      assigned_user_id: workstation.assigned_user_id ? String(workstation.assigned_user_id) : '',
      comments: workstation.comments || '',
      supplier_id: workstation.supplier_id ? String(workstation.supplier_id) : '',
    });
    setEditModal({ open: true, workstation: null, isCopy: true });
  };

  // Create new
  const handleCreate = () => {
    setFormData(initialFormData);
    setEditModal({ open: true, workstation: null, isCopy: false });
  };

  // Save
  const handleSave = async () => {
    if (!formData.computername.trim()) {
      toast.error('El nombre del equipo es requerido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        os: formData.os ? parseInt(formData.os) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        assigned_user_id: formData.assigned_user_id ? parseInt(formData.assigned_user_id) : null,
        cliente_id: formData.cliente_id ? parseInt(formData.cliente_id) : null,
        purchase_date: formData.purchase_date || null,
      };
      
      if (editModal.workstation && !editModal.isCopy) {
        await api.put(`/assets/workstations/${editModal.workstation.id}`, payload);
        toast.success('Workstation actualizada exitosamente');
      } else {
        await api.post('/assets/workstations', payload);
        toast.success('Workstation creada exitosamente');
      }
      setEditModal({ open: false, workstation: null, isCopy: false });
      fetchWorkstations();
      fetchStats();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/assets/workstations/${deleteModal.workstation.id}`);
      toast.success('Workstation eliminada exitosamente');
      setDeleteModal({ open: false, workstation: null });
      fetchWorkstations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver Workstations</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Workstations</h1>
            <p className="page-subtitle">Gestión de equipos de escritorio y laptops</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/workstations"
              filters={{ search: columnFilters.equipo }}
              filename="workstations"
            />
            {canCreate && (
              <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-workstation-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Workstation
              </Button>
            )}
          </div>
        </div>

        {/* Stats - Clickable cards to filter (4 cards: Total, Activo, Advertencia, Crítico, Sin Agente) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="workstation-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:scale-105 ${agenteFilter === null ? 'ring-2 ring-primary' : 'opacity-60 hover:opacity-100'}`}
            onClick={() => setAgenteFilter(null)}
            data-testid="stats-total"
          >
            <Monitor className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:scale-105 ${agenteFilter === 'activo' ? 'ring-2 ring-green-500' : 'opacity-60 hover:opacity-100'}`}
            onClick={() => handleAgenteFilterClick('activo')}
            data-testid="stats-activo"
          >
            <UserCheck className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.agente_activo}</div>
              <div className="stats-card-label">Activo</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:scale-105 ${agenteFilter === 'advertencia' ? 'ring-2 ring-yellow-500' : 'opacity-60 hover:opacity-100'}`}
            onClick={() => handleAgenteFilterClick('advertencia')}
            data-testid="stats-advertencia"
          >
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.agente_advertencia}</div>
              <div className="stats-card-label">Advertencia</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:scale-105 ${agenteFilter === 'critico' ? 'ring-2 ring-red-500' : 'opacity-60 hover:opacity-100'}`}
            onClick={() => handleAgenteFilterClick('critico')}
            data-testid="stats-critico"
          >
            <WifiOff className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.agente_critico}</div>
              <div className="stats-card-label">Crítico</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:scale-105 ${agenteFilter === 'sin_agente' ? 'ring-2 ring-gray-400' : 'opacity-60 hover:opacity-100'}`}
            onClick={() => handleAgenteFilterClick('sin_agente')}
            data-testid="stats-sin-agente"
          >
            <Activity className="h-6 w-6 text-gray-400 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-gray-400">{stats.sin_agente}</div>
              <div className="stats-card-label">Sin Agente</div>
            </div>
          </div>
        </div>
        
        {/* Active filter indicator */}
        {agenteFilter && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Filtrando por:</span>
            <span className={`badge ${
              agenteFilter === 'activo' ? 'badge-success' :
              agenteFilter === 'advertencia' ? 'badge-warning' :
              agenteFilter === 'critico' ? 'badge-danger' : 'badge-secondary'
            }`}>
              {agenteFilter === 'activo' ? 'Activo' :
               agenteFilter === 'advertencia' ? 'Advertencia' :
               agenteFilter === 'critico' ? 'Crítico' : 'Sin Agente'}
            </span>
            <button 
              onClick={() => setAgenteFilter(null)} 
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              Limpiar filtro
            </button>
          </div>
        )}

        {/* Search and Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[250px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar en todos los campos..."
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  className="pl-10"
                  data-testid="global-search-input"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      {/* Headers Row */}
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Seleccionar todos"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('computername')}
                        >
                          <div className="flex items-center gap-1">
                            EQUIPO
                            <SortIcon field="computername" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('cliente_name')}
                        >
                          <div className="flex items-center gap-1">
                            CLIENTE
                            <SortIcon field="cliente_name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('brand_name')}
                        >
                          <div className="flex items-center gap-1">
                            MARCA
                            <SortIcon field="brand_name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('modelo')}
                        >
                          <div className="flex items-center gap-1">
                            MODELO
                            <SortIcon field="modelo" />
                          </div>
                        </th>
                        <th 
                          className="hidden lg:table-cell cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('os_name')}
                        >
                          <div className="flex items-center gap-1">
                            OS
                            <SortIcon field="os_name" />
                          </div>
                        </th>
                        <th 
                          className="hidden xl:table-cell cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('os_caption')}
                        >
                          <div className="flex items-center gap-1">
                            EDICIÓN
                            <SortIcon field="os_caption" />
                          </div>
                        </th>
                        <th 
                          className="hidden xl:table-cell cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('os_build')}
                        >
                          <div className="flex items-center gap-1">
                            BUILD
                            <SortIcon field="os_build" />
                          </div>
                        </th>
                        <th 
                          className="hidden lg:table-cell cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('site_name')}
                        >
                          <div className="flex items-center gap-1">
                            SITE
                            <SortIcon field="site_name" />
                          </div>
                        </th>
                        <th 
                          className="hidden lg:table-cell cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('assigned_user_name')}
                        >
                          <div className="flex items-center gap-1">
                            USUARIO
                            <SortIcon field="assigned_user_name" />
                          </div>
                        </th>
                        <th 
                          className="hidden lg:table-cell cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('usuario_legajo')}
                        >
                          <div className="flex items-center gap-1">
                            LEGAJO
                            <SortIcon field="usuario_legajo" />
                          </div>
                        </th>
                        <th 
                          className="text-center cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ram')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            RAM
                            <SortIcon field="ram" />
                          </div>
                        </th>
                        <th className="w-28 text-center">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.equipo}
                            onChange={(e) => handleFilterChange('equipo', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-equipo"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.cliente}
                            onChange={(e) => handleFilterChange('cliente', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-cliente"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.marca}
                            onChange={(e) => handleFilterChange('marca', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-marca"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.modelo}
                            onChange={(e) => handleFilterChange('modelo', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-modelo"
                          />
                        </th>
                        <th className="py-2 px-4 hidden lg:table-cell">
                          <Select
                            value={columnFilters.os_id || undefined}
                            onValueChange={(v) => handleFilterChange('os_id', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background" data-testid="filter-os">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {formOptions.os?.map(o => (
                                <SelectItem key={o.id} value={o.id.toString()}>{o.description}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4 hidden xl:table-cell">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.edicion}
                            onChange={(e) => handleFilterChange('edicion', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-edicion"
                          />
                        </th>
                        <th className="py-2 px-4 hidden xl:table-cell">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.build}
                            onChange={(e) => handleFilterChange('build', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-build"
                          />
                        </th>
                        <th className="py-2 px-4 hidden lg:table-cell">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.site}
                            onChange={(e) => handleFilterChange('site', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-site"
                          />
                        </th>
                        <th className="py-2 px-4 hidden lg:table-cell">
                          <Select
                            value={columnFilters.usuario_id || undefined}
                            onValueChange={(v) => handleFilterChange('usuario_id', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background" data-testid="filter-usuario">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {formOptions.usuarios?.map(u => (
                                <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4 hidden lg:table-cell">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.legajo}
                            onChange={(e) => handleFilterChange('legajo', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-legajo"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.ram}
                            onChange={(e) => handleFilterChange('ram', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-ram"
                          />
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {workstations.length === 0 ? (
                        <tr>
                          <td colSpan={13}>
                            <div className="empty-state">
                              <Monitor className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron workstations</p>
                            </div>
                          </td>
                        </tr>
                      ) : workstations.map((wks) => (
                        <tr key={wks.id} data-testid={`wks-row-${wks.id}`} className={isSelected(wks.id) ? 'bg-primary/5' : ''}>
                          <td className="w-10">
                            <Checkbox
                              checked={isSelected(wks.id)}
                              onCheckedChange={() => toggleRow(wks.id)}
                              aria-label={`Seleccionar ${wks.computername}`}
                            />
                          </td>
                          <td>
                            <div className="flex items-center gap-1.5">
                              <Link 
                                to={`/micro/workstations/${wks.id}`}
                                className="font-medium text-primary hover:underline"
                              >
                                {wks.computername}
                              </Link>
                              {wks.has_hardware ? (
                                <HardwareInfo hostname={wks.computername} updatedAt={wks.hardware_last_update} />
                              ) : null}
                              {wks.hw_type && (
                                <span className="text-xs text-muted-foreground">({wks.hw_type})</span>
                              )}
                            </div>
                          </td>
                          <td className="text-muted-foreground">{wks.cliente_name || '-'}</td>
                          <td className="text-muted-foreground">{wks.brand_name || '-'}</td>
                          <td className="text-muted-foreground">{wks.modelo || '-'}</td>
                          <td className="text-muted-foreground hidden lg:table-cell text-sm">{wks.os_name || '-'}</td>
                          <td className="text-muted-foreground hidden xl:table-cell text-sm">{wks.os_caption || '-'}</td>
                          <td className="hidden xl:table-cell font-mono text-xs text-primary">{wks.os_build || wks.os_build_short || '-'}</td>
                          <td className="text-muted-foreground hidden lg:table-cell">{wks.site_name || '-'}</td>
                          <td className="text-muted-foreground hidden lg:table-cell">{wks.assigned_user_name || '-'}</td>
                          <td className="text-muted-foreground hidden lg:table-cell">{wks.usuario_legajo || '-'}</td>
                          <td className="text-center">
                            {wks.ram ? (
                              <span className="badge badge-info">{parseFloat(wks.ram).toFixed(0)} GB</span>
                            ) : '-'}
                          </td>
                          <td className="w-28">
                            <div className="flex justify-center gap-0.5">
                              <Link to={`/micro/workstations/${wks.id}`}>
                                <button
                                  className="action-btn"
                                  title="Ver detalle"
                                  data-testid={`view-wks-${wks.id}`}
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </Link>
                              <button
                                onClick={() => canUpdate && handleEdit(wks)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-wks-${wks.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(wks)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title="Copiar"
                                data-testid={`copy-wks-${wks.id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, workstation: wks })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-wks-${wks.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      {pagination.total > 0 ? `${(pagination.page - 1) * pagination.page_size + 1} - ${Math.min(pagination.page * pagination.page_size, pagination.total)} de ${pagination.total}` : '0 registros'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pagination.page_size.toString()}
                        onValueChange={(value) => setPagination(p => ({ ...p, page_size: parseInt(value), page: 1 }))}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{pagination.page} / {pagination.pages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                      disabled={pagination.page === pagination.pages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit/Create/Copy Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, workstation: null, isCopy: false })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5 text-primary" />
                {editModal.isCopy ? 'Copiar Workstation' : editModal.workstation ? 'Editar Workstation' : 'Nueva Workstation'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Unique fields */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Nombre Equipo *</Label>
                <Input
                  value={formData.computername}
                  onChange={(e) => setFormData(prev => ({ ...prev, computername: e.target.value }))}
                  placeholder="PC-001"
                  data-testid="input-computername"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo único</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Modelo</Label>
                <Input
                  value={formData.modelo}
                  onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                  placeholder="Dell Latitude 5520"
                />
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Nro. Serie</Label>
                <Input
                  value={formData.serialnumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, serialnumber: e.target.value }))}
                  placeholder="ABC123XYZ"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo único</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Marca</Label>
                <Select
                  value={formData.brand_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.brands?.map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Sistema Operativo</Label>
                <Select
                  value={formData.os || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, os: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.os?.map(o => (
                      <SelectItem key={o.id} value={String(o.id)}>{o.description || o.os}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">CPU</Label>
                <Input
                  value={formData.cpu}
                  onChange={(e) => setFormData(prev => ({ ...prev, cpu: e.target.value }))}
                  placeholder="Intel Core i5-10210U"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">RAM (GB)</Label>
                <Input
                  value={formData.ram}
                  onChange={(e) => setFormData(prev => ({ ...prev, ram: e.target.value }))}
                  placeholder="16"
                />
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">MAC Address</Label>
                <Input
                  value={formData.macaddress}
                  onChange={(e) => setFormData(prev => ({ ...prev, macaddress: e.target.value }))}
                  placeholder="00:1A:2B:3C:4D:5E"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo único</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Fecha Compra</Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <Select
                  value={formData.status_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.estados?.map(e => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Proveedor</Label>
                <Select
                  value={formData.supplier_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.proveedores?.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Empresa</Label>
                <Select
                  value={formData.empresa_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.empresas?.map(e => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Site</Label>
                <Select
                  value={formData.site_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.sites?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Ubicación</Label>
                <Select
                  value={formData.location_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.ubicaciones?.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Usuario</Label>
                <Select
                  value={formData.assigned_user_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_user_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.usuarios?.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Cliente</Label>
                <Select
                  value={formData.cliente_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, cliente_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin seleccionar</SelectItem>
                    {formOptions.clientes?.map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Comentarios</Label>
                <Textarea
                  value={formData.comments}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                  placeholder="Notas adicionales..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, workstation: null, isCopy: false })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, workstation: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Workstation
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              ¿Estás seguro que deseas eliminar la workstation <strong>{deleteModal.workstation?.computername}</strong>?
            </p>
            <p className="text-sm text-yellow-600">
              Nota: Los datos de detalle (software, parches, etc.) se conservarán en el historial.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, workstation: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchWorkstations(); fetchStats(); }}
          endpoint="/assets/workstations"
          options={{
            sites: formOptions.sites,
            empresas: formOptions.empresas,
            statuses: formOptions.estados,
            ubicaciones: formOptions.ubicaciones,
            usuarios: formOptions.usuarios
          }}
          fields={['site_id', 'empresa_id', 'status_id', 'location_id', 'assigned_user_id']}
        />
      </div>
    </Layout>
  );
};

export default Workstations;
